import { Link } from 'react-router-dom';
import s from './LandingPage.module.css';

/* -------------------------------------------------------------------------- */
/*  Data                                                                       */
/* -------------------------------------------------------------------------- */

const FEATURES = [
  {
    title: 'DMARC Report Analysis',
    desc: 'Automatically ingest and parse aggregate reports from every major mailbox provider.',
    color: 'var(--accent)',
  },
  {
    title: 'DNS Change Monitoring',
    desc: 'Continuous checks on SPF, DKIM, and DMARC records with alerts on unexpected changes.',
    color: 'var(--info)',
  },
  {
    title: 'Sending Source Identification',
    desc: 'See every IP and service sending email as your domain, grouped and labeled.',
    color: 'var(--success)',
  },
  {
    title: 'Multi-domain Management',
    desc: 'Manage all your domains in one place with per-domain dashboards and controls.',
    color: 'var(--chart-5)',
  },
  {
    title: 'Audit Trail & Security',
    desc: 'Full audit log of every change, login, and configuration update for compliance.',
    color: 'var(--warning)',
  },
  {
    title: 'Usage-based Pricing',
    desc: 'Pay only for what you use. Free tier included, no credit card required.',
    color: 'var(--danger)',
  },
] as const;

const PRICING = [
  {
    plan: 'Free',
    price: '$0',
    period: '/month',
    desc: 'For individuals getting started with DMARC.',
    features: ['Up to 1,000 reports/month', '2 domains', 'Community support', '7-day data retention'],
    recommended: false,
  },
  {
    plan: 'Pro',
    price: '$29',
    period: '/month',
    desc: 'For teams serious about email authentication.',
    features: ['Up to 50,000 reports/month', '25 domains', 'Email support', '90-day data retention'],
    recommended: true,
  },
  {
    plan: 'Business',
    price: '$99',
    period: '/month',
    desc: 'For organizations with advanced needs.',
    features: ['Unlimited reports', 'Unlimited domains', 'Priority support', '1-year data retention'],
    recommended: false,
  },
] as const;

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function LandingPage() {
  return (
    <div>
      {/* -- Nav ----------------------------------------------------------- */}
      <nav className={s.nav}>
        <div className={s.navInner}>
          <span className={s.logo}>DMARCopolo</span>

          <ul className={s.navLinks}>
            <li><a href="#features" className={s.navLink}>Features</a></li>
            <li><a href="#pricing" className={s.navLink}>Pricing</a></li>
          </ul>

          <div className={s.navActions}>
            <Link to="/login" className={s.btnLink}>Log In</Link>
            <Link to="/signup" className={s.btnAccent}>Sign Up</Link>
          </div>
        </div>
      </nav>

      {/* -- Hero ---------------------------------------------------------- */}
      <section className={`${s.section} ${s.hero}`}>
        <h1 className={s.heroHeadline}>
          Stop flying blind with your email authentication
        </h1>
        <p className={s.heroSubtext}>
          DMARCopolo ingests your DMARC reports and monitors your DNS records so
          you can see exactly who is sending email as your domains.
        </p>

        <div className={s.heroActions}>
          <Link to="/signup" className={s.btnAccent}>Get Started Free</Link>
          <a href="#pricing" className={s.btnGhost}>See Pricing</a>
        </div>

        <div className={s.heroFlow}>
          <span className={s.flowHighlight}>Marco?</span>
          <span className={s.flowArrow}>&rarr;</span>
          <span>DNS Record</span>
          <span className={s.flowArrow}>&rarr;</span>
          <span>Receivers</span>
          <span className={s.flowArrow}>&rarr;</span>
          <span>Reports</span>
          <span className={s.flowArrow}>&rarr;</span>
          <span className={s.flowHighlight}>Polo!</span>
        </div>
      </section>

      {/* -- How It Works -------------------------------------------------- */}
      <section className={`${s.section} ${s.howItWorks}`} id="how-it-works">
        <h2 className={s.sectionTitle}>How it works</h2>
        <p className={s.sectionSubtitle}>Three steps to full DMARC visibility.</p>

        <div className={s.steps}>
          <div className={s.step}>
            <div className={s.stepNumber}>1</div>
            <h3 className={s.stepTitle}>Add your domains</h3>
            <p className={s.stepDesc}>
              Enter each domain you want to monitor. We generate a unique
              reporting address for each one.
            </p>
          </div>

          <div className={s.step}>
            <div className={s.stepNumber}>2</div>
            <h3 className={s.stepTitle}>Publish your DMARC record</h3>
            <p className={s.stepDesc}>
              Add a single DNS TXT record pointing your DMARC reports to us.
              We give you the exact record to copy.
            </p>
            <div className={s.stepCode}>
              v=DMARC1; p=none; rua=mailto:abc123@reports.dmarcopolo.com
            </div>
          </div>

          <div className={s.step}>
            <div className={s.stepNumber}>3</div>
            <h3 className={s.stepTitle}>Gain clarity</h3>
            <p className={s.stepDesc}>
              Reports flow in automatically. DNS records are monitored
              continuously. Sending sources are identified and grouped.
            </p>
          </div>
        </div>
      </section>

      {/* -- Features ------------------------------------------------------ */}
      <section className={`${s.section} ${s.features}`} id="features">
        <h2 className={s.sectionTitle}>Features</h2>
        <p className={s.sectionSubtitle}>
          Everything you need to manage email authentication across your domains.
        </p>

        <div className={s.featureGrid}>
          {FEATURES.map((f) => (
            <div key={f.title} className={s.featureCard}>
              <div
                className={s.featureIcon}
                style={{ backgroundColor: f.color }}
              />
              <h3 className={s.featureTitle}>{f.title}</h3>
              <p className={s.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* -- Pricing ------------------------------------------------------- */}
      <section className={`${s.section} ${s.pricing}`} id="pricing">
        <h2 className={s.sectionTitle}>Pricing</h2>
        <p className={s.sectionSubtitle}>
          Start free. Upgrade when you need to.
        </p>

        <div className={s.pricingGrid}>
          {PRICING.map((tier) => (
            <div
              key={tier.plan}
              className={
                tier.recommended
                  ? s.pricingCardRecommended
                  : s.pricingCard
              }
            >
              {tier.recommended && (
                <span className={s.pricingBadge}>Recommended</span>
              )}
              <h3 className={s.pricingPlan}>{tier.plan}</h3>
              <div className={s.pricingPrice}>
                {tier.price}
                <span className={s.pricingPeriod}>{tier.period}</span>
              </div>
              <p className={s.pricingDesc}>{tier.desc}</p>
              <ul className={s.pricingFeatures}>
                {tier.features.map((feat) => (
                  <li key={feat} className={s.pricingFeature}>{feat}</li>
                ))}
              </ul>
              <Link
                to="/signup"
                className={tier.recommended ? s.btnAccent : s.btnGhost}
                style={{ width: '100%' }}
              >
                {tier.plan === 'Free' ? 'Get Started' : 'Start Free Trial'}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* -- Footer -------------------------------------------------------- */}
      <footer className={s.footer}>
        <div className={s.footerInner}>
          <span className={s.footerText}>
            Built on Cloudflare's global edge &middot; &copy; 2026 DMARCopolo
          </span>
          <ul className={s.footerLinks}>
            <li><a href="#" className={s.footerLink}>Privacy</a></li>
            <li><a href="#" className={s.footerLink}>Terms</a></li>
            <li><a href="#" className={s.footerLink}>Status</a></li>
            <li><a href="#" className={s.footerLink}>Docs</a></li>
          </ul>
        </div>
      </footer>
    </div>
  );
}
